/**
 * Allowlist for third-party / user rich text (event & listing descriptions).
 * Shared by the server-side sanitizer (Tourdata ingest + API writes) and the
 * app's render-time sanitizer so the two can never drift. Shaped for
 * sanitize-html's options; both consumers pass it straight in.
 */
export const RICH_TEXT_SANITIZE_OPTIONS = {
  allowedTags: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'a',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'span',
    'div',
  ],
  allowedAttributes: { a: ['href'] },
  allowedSchemes: ['http', 'https', 'mailto'],
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
}

const MAX_CODE_POINT = 0x10ffff

/**
 * Single-pass HTML entity decode, equivalent to a server-side HtmlDecode.
 * One non-overlapping scan: double-escaped input like `&amp;nbsp;` decodes to
 * `&nbsp;` (not a literal space) and `&lt;p&gt;` to `<p>`. Used to undo the one
 * level of escaping Tourdata applies before the result is sanitized as HTML.
 */
export function decodeHtmlEntities(input: string): string {
  return input.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);/g, (match, entity: string) => {
    if (entity[0] === '#') {
      const codePoint =
        entity[1] === 'x' || entity[1] === 'X'
          ? Number.parseInt(entity.slice(2), 16)
          : Number.parseInt(entity.slice(1), 10)
      if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > MAX_CODE_POINT) {
        return match
      }
      return String.fromCodePoint(codePoint)
    }
    return NAMED_ENTITIES[entity.toLowerCase()] ?? match
  })
}

const HTML_TAG = /<\/?[a-z][a-z0-9]*(?:\s[^<>]*)?\/?>/i
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi
const TRAILING_PUNCTUATION = /[.,;:!?'")\]]+$/

/** True when the text contains at least one HTML tag, i.e. it is rich text rather than plain text. */
export function hasHtmlTags(text: string): boolean {
  return HTML_TAG.test(text)
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function linkify(text: string): string {
  let out = ''
  let last = 0
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0
    const trailing = match[0].match(TRAILING_PUNCTUATION)?.[0] ?? ''
    const url = match[0].slice(0, match[0].length - trailing.length)
    if (!url) continue
    const href = url.toLowerCase().startsWith('www.') ? `https://${url}` : url
    out += `${escapeHtml(text.slice(last, start))}<a href="${escapeHtml(href)}">${escapeHtml(url)}</a>`
    last = start + url.length
  }
  return out + escapeHtml(text.slice(last))
}

/**
 * Convert a plain-text description to rich-text HTML: escapes markup, wraps
 * blank-line separated blocks in `<p>`, turns single newlines into `<br>` and
 * links bare `http(s)://` / `www.` URLs. Returns '' for blank input.
 */
export function plainTextToHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n[^\S\n]*\n\s*/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map(
      (block) =>
        `<p>${block
          .split(/[^\S\n]*\n[^\S\n]*/)
          .map(linkify)
          .join('<br>')}</p>`,
    )
    .join('')
}
